import { useState, useEffect, useCallback } from 'react';

interface AnalysisState {
  selectedImage?: File | null;
  imagePreview?: string | null;
  analysisResult?: any | null;
  isProcessing?: boolean;
  currentStep?: number;
  progress?: number;
  error?: string | null;
  startTime?: number;
}

interface UsePageVisibilityAnalysisOptions {
  persistKey: string;
  maxPersistTime?: number; // Maximum time to persist state (default: 30 minutes)
  onVisibilityChange?: (isVisible: boolean) => void;
}

export const usePageVisibilityAnalysis = ({
  persistKey,
  maxPersistTime = 30 * 60 * 1000, // 30 minutes
  onVisibilityChange
}: UsePageVisibilityAnalysisOptions) => {
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const [lastVisibilityChange, setLastVisibilityChange] = useState(Date.now());

  // Save analysis state to localStorage
  const saveAnalysisState = useCallback((state: AnalysisState) => {
    try {
      const stateWithTimestamp = {
        ...state,
        timestamp: Date.now(),
        startTime: state.startTime || Date.now()
      };
      localStorage.setItem(persistKey, JSON.stringify(stateWithTimestamp));
    } catch (error) {
      console.warn('Failed to save analysis state:', error);
    }
  }, [persistKey]);

  // Load analysis state from localStorage
  const loadAnalysisState = useCallback((): AnalysisState | null => {
    try {
      const stored = localStorage.getItem(persistKey);
      if (!stored) return null;

      const state = JSON.parse(stored);
      const timeSinceStore = Date.now() - (state.timestamp || 0);

      // Check if stored state is too old
      if (timeSinceStore > maxPersistTime) {
        localStorage.removeItem(persistKey);
        return null;
      }

      // Convert base64 back to File if image was stored
      if (state.selectedImage && typeof state.selectedImage === 'string') {
        // For security and memory reasons, we don't persist the actual File object
        // Instead, we just mark that there was an image selected
        state.selectedImage = null;
      }

      return state;
    } catch (error) {
      console.warn('Failed to load analysis state:', error);
      return null;
    }
  }, [persistKey, maxPersistTime]);

  // Clear analysis state from localStorage
  const clearAnalysisState = useCallback(() => {
    try {
      localStorage.removeItem(persistKey);
    } catch (error) {
      console.warn('Failed to clear analysis state:', error);
    }
  }, [persistKey]);

  // Handle visibility change
  const handleVisibilityChange = useCallback(() => {
    const visible = !document.hidden;
    setIsVisible(visible);
    setLastVisibilityChange(Date.now());
    
    onVisibilityChange?.(visible);
    
    // If page becomes visible and we've been away for less than 30 minutes,
    // the state should still be valid
    if (visible) {
      const timeSinceHidden = Date.now() - lastVisibilityChange;
      if (timeSinceHidden > maxPersistTime) {
        clearAnalysisState();
      }
    }
  }, [onVisibilityChange, lastVisibilityChange, maxPersistTime, clearAnalysisState]);

  // Setup visibility change listener
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also listen for beforeunload to save state
    const handleBeforeUnload = () => {
      // State will be saved by the component using this hook
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleVisibilityChange]);

  // Check if analysis was in progress when returning to tab
  const checkForResumableAnalysis = useCallback(() => {
    const savedState = loadAnalysisState();
    if (savedState && savedState.isProcessing) {
      // Analysis was in progress, but we can't resume it
      // Return the state but mark as not processing
      return {
        ...savedState,
        isProcessing: false,
        error: 'Analysis was interrupted when you switched tabs. Please try again.'
      };
    }
    return savedState;
  }, [loadAnalysisState]);

  return {
    isVisible,
    lastVisibilityChange,
    saveAnalysisState,
    loadAnalysisState,
    clearAnalysisState,
    checkForResumableAnalysis
  };
};

// Custom hook for analysis components to handle tab switching gracefully
export const useAnalysisPageVisibility = (apiProvider: string) => {
  const persistKey = `analysis_state_${apiProvider}`;
  
  const {
    isVisible,
    saveAnalysisState,
    clearAnalysisState,
    checkForResumableAnalysis
  } = usePageVisibilityAnalysis({
    persistKey,
    maxPersistTime: 30 * 60 * 1000, // 30 minutes
    onVisibilityChange: (visible) => {
      if (visible) {
        console.log(`Analysis page became visible for ${apiProvider}`);
      } else {
        console.log(`Analysis page became hidden for ${apiProvider}`);
      }
    }
  });

  const [wasAwayDuringAnalysis, setWasAwayDuringAnalysis] = useState(false);
  const [analysisStateBeforeHide, setAnalysisStateBeforeHide] = useState<AnalysisState | null>(null);

  // Check for resumable analysis on mount
  useEffect(() => {
    const savedState = checkForResumableAnalysis();
    if (savedState && (savedState.isProcessing || savedState.error)) {
      setWasAwayDuringAnalysis(true);
      setAnalysisStateBeforeHide(savedState);
    }
  }, [checkForResumableAnalysis]);

  // Monitor visibility changes and warn users
  useEffect(() => {
    let visibilityWarningTimeout: NodeJS.Timeout;
    
    if (!isVisible && analysisStateBeforeHide?.isProcessing) {
      // Show warning after 3 seconds of being hidden during analysis
      visibilityWarningTimeout = setTimeout(() => {
        console.warn('User has been away from analysis tab for 3+ seconds during processing');
      }, 3000);
    }
    
    return () => {
      if (visibilityWarningTimeout) {
        clearTimeout(visibilityWarningTimeout);
      }
    };
  }, [isVisible, analysisStateBeforeHide]);

  // Provide methods to save/restore specific analysis state
  const saveCurrentAnalysis = useCallback((state: AnalysisState) => {
    setAnalysisStateBeforeHide(state);
    saveAnalysisState(state);
  }, [saveAnalysisState]);

  const restoreAnalysisIfNeeded = useCallback(() => {
    if (wasAwayDuringAnalysis && analysisStateBeforeHide) {
      setWasAwayDuringAnalysis(false);
      const restoredState = { ...analysisStateBeforeHide };
      
      // If analysis was in progress, mark it as interrupted
      if (restoredState.isProcessing) {
        restoredState.isProcessing = false;
        restoredState.error = 'Analysis was interrupted when you switched tabs. Please try again.';
      }
      
      setAnalysisStateBeforeHide(null);
      return restoredState;
    }
    return null;
  }, [wasAwayDuringAnalysis, analysisStateBeforeHide]);

  const markAnalysisComplete = useCallback(() => {
    clearAnalysisState();
    setWasAwayDuringAnalysis(false);
    setAnalysisStateBeforeHide(null);
  }, [clearAnalysisState]);

  // Check if user was away long enough to warrant a warning
  const shouldShowWarning = wasAwayDuringAnalysis && analysisStateBeforeHide?.isProcessing;

  return {
    isVisible,
    wasAwayDuringAnalysis,
    shouldShowWarning,
    saveCurrentAnalysis,
    restoreAnalysisIfNeeded,
    markAnalysisComplete,
    clearAnalysisState
  };
};
